import { Button } from '@/components/ui/Button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandInputProps,
  CommandItem,
  CommandList
} from '@/components/ui/Command';
import { inputVariants } from '@/components/ui/Input';
import withLocation from '@/components/withLocation';
import { getSearchPages } from '@/lib/helpers/search';
import { cn } from '@/lib/helpers/utils';
import { useMediaQuery } from '@/lib/hooks';
import { NavigateFn, WindowLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { ArrowRight, X } from 'lucide-react';
import React, { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { SearchPageInfo } from '../../../types/search';

type SearchInputQuickFilterOption = { path: string; value: string; label: React.ReactNode };
type SearchPageQuickSearch = Partial<SearchPageInfo> & { suffix: string };

const getSearchInputQuickFilterOptions = (value: string, baseSearchPath: string): SearchInputQuickFilterOption[] => {
  let pages: any = getSearchPages().map((search) => {
    return { ...search, ...{ suffix: `in ${search.label}` } };
  });
  pages.unshift({ suffix: 'show me everything', alias: baseSearchPath });

  return pages.map((page: SearchPageQuickSearch) => {
    return {
      path: page.alias,
      value,
      label: (
        <>
          <span className="font-semibold">{value}</span>
          {page.suffix}
        </>
      )
    } as SearchInputQuickFilterOption;
  });
};

interface SearchInputQuickFilterProps extends Omit<CommandInputProps, 'value'> {
  location: WindowLocation<unknown>;
  params: URLSearchParams;
  baseSearchPath: string;
  setIsSearchOpen: (open: boolean) => void;
}

const SearchInputQuickFilter = ({
  location,
  params,
  placeholder,
  baseSearchPath,
  setIsSearchOpen,
  ...props
}: SearchInputQuickFilterProps) => {
  const key: string = params && params.has('search') ? (params.get('search') as string) : '';
  const inputRef = useRef<HTMLInputElement>(null);

  const isDesktop = useMediaQuery();
  const [isOpen, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(key);

  const [options, setOptions] = useState<SearchInputQuickFilterOption[]>(
    getSearchInputQuickFilterOptions(key, baseSearchPath)
  );

  /**
   * Actions
   */
  const closeContainer = () => setIsSearchOpen(false);
  const onSearch = (
    optionValue: SearchInputQuickFilterOption | string,
    baseSearchPath: string,
    navigate: NavigateFn
  ) => {
    if (typeof optionValue === 'string') {
      navigate(`${baseSearchPath}?search=${optionValue}`);
    } else {
      navigate(`${optionValue.path}?search=${optionValue.value}`);
    }
    closeContainer();
  };

  /**
   * Handlers
   */
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    event.key === 'Escape' && inputRef?.current?.blur();
  }, []);

  const handleOnChange = React.useCallback((value: string) => {
    if (value.length > 1) {
      setOptions(getSearchInputQuickFilterOptions(value, baseSearchPath));
      setOpen(true);
    } else {
      setOptions([]);
    }
    setInputValue(value);
  }, []);

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClear = useCallback(() => {
    inputValue.length > 0 && onSearch('', baseSearchPath, navigate);
    setInputValue('');
    setOpen(false);
  }, []);

  const handleSelectOption = useCallback((selectedOption: SearchInputQuickFilterOption) => {
    setInputValue(selectedOption.value);
    onSearch(selectedOption, baseSearchPath, navigate);
    setTimeout(() => {
      inputRef?.current?.blur();
    }, 0);
  }, []);

  return (
    <Command
      onKeyDown={handleKeyDown}
      style={{ overflow: 'unset' }}
      filter={(value, search) => {
        return 1;
      }}
    >
      <div className="relative flex space-x-2">
        <CommandInput
          ref={inputRef}
          value={inputValue}
          onValueChange={handleOnChange}
          onBlur={handleBlur}
          onFocus={() => inputValue.length > 1 && setOpen(true)}
          placeholder={placeholder}
          className={cn(inputVariants({ size: isDesktop ? 'md' : 'sm', variant: 'filter' }))}
          data-cy="field:quick-search"
          {...props}
        />
        <div className="absolute space-x-1 space-x-reverse right-2 top-1 flex flex-row-reverse items-center md:space-x-2 md:space-x-reverse md:top-1.5">
          <Button
            type="submit"
            aria-label="Submit quick search"
            icon
            appearance="primary"
            size={isDesktop ? 'sm' : 'xs'}
            onClick={() => onSearch(inputValue, baseSearchPath, navigate)}
            onKeyDown={(event: KeyboardEvent) =>
              event.key === 'Enter' && onSearch(inputValue, baseSearchPath, navigate)
            }
            disabled={inputValue.length < 2}
            data-cy="button:quick-search-search"
          >
            <ArrowRight className="w-5" />
          </Button>
          <Button
            type="button"
            aria-label="Clear quick search"
            className={cn()}
            onClick={handleClear}
            icon={true}
            size="xs"
            disabled={inputValue.length === 0}
            variant="ghost"
            data-cy="button:quick-search-clear"
          >
            <X className="w-4" />
          </Button>
        </div>
      </div>
      {options.length > 0 && isOpen && (
        <div className="relative mt-1">
          <div
            className={cn(
              'animate-in fade-in-0 absolute top-0 z-10 w-full rounded-lg bg-background border-gray-200 border outline-none block shadow'
            )}
          >
            <CommandList className="p-3">
              <CommandGroup>
                {options.map((option) => {
                  return (
                    <CommandItem
                      key={option.path}
                      value={option.path}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      aria-selected={false}
                      data-selected={false}
                      onSelect={() => handleSelectOption(option)}
                      className={cn('flex rounded-lg w-full gap-x-1 px-3 py-2 cursor-pointer')}
                    >
                      {option.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </div>
        </div>
      )}
    </Command>
  );
};

export default withLocation(SearchInputQuickFilter);
